import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from '../services/api.service';
import {
  Evaluation,
  EvaluationReviewPayload,
  EvaluationWritePayload,
  normalizeEvaluation,
  normalizeEvaluations
} from './evaluation.model';

/**
 * CMS API contract for employee evaluations (`cms/api/v1/evaluations`).
 *
 * GET    evaluations/            admin list
 * GET    evaluations/me          evaluations for the signed-in employee
 * GET    evaluations/:id
 * POST   evaluations/            employee creates a draft or submission
 * PATCH  evaluations/:id         employee update, or admin edit of the answers
 * PATCH  evaluations/:id/review  admin decision
 *
 * Write body:
 * {
 *   employee_id, employee_name,
 *   form_type: 'housekeeping' | 'physiotherapy' | 'receptionist',
 *   period_from, period_to,  // a calendar quarter: 1 Jan–31 Mar, 1 Apr–30 Jun, 1 Jul–30 Sep, 1 Oct–31 Dec
 *   answers,            // question key -> value for that form
 *   employee_notes,     // copy of answers.recommendations
 *   status: 'draft' | 'submitted' | 'edit_requested' | 'approved' | 'rejected',
 *   edited_by_admin     // true when an admin saves the form themselves
 * }
 *
 * Review body:
 * { action: 'approve' | 'reject' | 'request_edit', comment }
 *
 * A review should store `admin_comment`, `reviewed_at`, and the new status.
 * `request_edit` sets status to `edit_requested` so the employee can change and resubmit.
 */
@Injectable({
  providedIn: 'root'
})
export class EvaluationApiService {

  constructor(private api: ApiService) { }

  list(token: string | null): Observable<Evaluation[]> {
    return this.api.get('evaluations/', token).pipe(
      map(data => normalizeEvaluations(data))
    );
  }

  mine(token: string | null): Observable<Evaluation[]> {
    return this.api.get('evaluations/me', token).pipe(
      map(data => normalizeEvaluations(data))
    );
  }

  getOne(id: string, token: string | null): Observable<Evaluation | null> {
    return this.api.get('evaluations/' + id, token).pipe(
      map(data => normalizeEvaluation(data))
    );
  }

  create(body: EvaluationWritePayload, token: string | null): Observable<unknown> {
    return this.api.post('evaluations/', body, token);
  }

  update(id: string, body: EvaluationWritePayload, token: string | null): Observable<unknown> {
    return this.api.update('evaluations/' + id, body, token);
  }

  review(id: string, body: EvaluationReviewPayload, token: string | null): Observable<unknown> {
    return this.api.update('evaluations/' + id + '/review', body, token);
  }
}
